import React, { useState, useEffect, useCallback, useRef } from 'react'
// import {findDOMNode} from 'react-dom'
import Icon from '../../wui-icon/src'
import classnames from 'classnames';
import type {CalendarHeaderSelectProps} from './iCalendar';
import generate from './Generate';
const {generateM, getDisplayYear} = generate;

const CalendarHeaderSelect: React.FC<CalendarHeaderSelectProps> = (props) => {
    const { value, setSelectValue, type, lang, prefixCls, localeData } = props;

    // State management
    const [maskShow, setMaskShow] = useState(false);
    const [activeYearNum, setActiveYearNum] = useState(0);
    const [activeMonthNum, setActiveMonthNum] = useState(0);
    const [activeDateNum, setActiveDateNum] = useState(value.date() - 1);
    const [dateDom, setDateDom] = useState<React.ReactNode>(null);

    // Refs
    const yearUl = useRef<HTMLDivElement | null>(null);
    const monthUl = useRef<HTMLDivElement | null>(null);
    const dateUl = useRef<HTMLDivElement | null>(null);
    const boxDom = useRef<HTMLDivElement | null>(null);
    const mounted = useRef(true);

    // 判断n是否在root内
    const contains = useCallback((root: any, n: any) => {
        let node: any = n;
        while (node) {
            if (node === root) return true;
            node = node.parentNode;
        }
        return false;
    }, []);
    // 点击contextmenu以外的地方将其关闭
    const handleDocumentClick = (event: MouseEvent) => {
        if (!mounted.current) return;// 解决IE下报错findDOMNode
        const target = event.target as Node;
        if (boxDom.current && !contains(boxDom.current, target)) {
            setMaskShow(false);
        }
    };

    // Effect for mounting/unmounting
    useEffect(() => {
        mounted.current = true;
        document.addEventListener('click', handleDocumentClick);

        return () => {
            mounted.current = false;
            document.removeEventListener('click', handleDocumentClick);
        };
    }, [contains]);

    // 判断是否是闰年
    const isRunYear = useCallback((year: number) => {
        let flag = false;
        if (year % 4 == 0 && year % 100 != 0 || year % 400 == 0) {
            flag = true;
        }
        return flag;
    }, []);

    const onChangeYear = useCallback((val: string, index: number) => {
        setActiveYearNum(index);
        const newValue = generateM()(value).set('year', parseInt(val));
        setSelectValue(newValue);
    }, [value, setSelectValue]);

    // 点击日
    const onChangeDate = useCallback((index: number) => {
        setMaskShow(false);
        setActiveDateNum(index);
        const newValue = generateM()(value).set('date', index + 1);
        setSelectValue(newValue);
    }, [value, setSelectValue]);

    const getDateSelect = useCallback((year: number, month: number) => {
        let dateNum = 31;
        if (month === 4 || month === 6 || month === 9 || month === 11) {
            dateNum = 30;
        }
        if (month === 2) {
            if (isRunYear(year)) {
                dateNum = 29;
            } else {
                dateNum = 28;
            }
        }
        const node = (
            <div className={`${prefixCls}-header-select-date`} ref={dateUl}>
                <ul>
                    {[...new Array(dateNum)].map((_item, index: number) => {
                        return (
                            <li
                                className={
                                    activeDateNum == index
                                        ? `${prefixCls}-header-select-date-active`
                                        : undefined
                                }
                                key={index}
                                onClick={() => onChangeDate(index)}
                            >
                                {index + 1}
                                {lang === 'zh-cn' || lang === 'zh-tw'
                                    ? activeDateNum == index
                                        ? ' 日'
                                        : ''
                                    : ''}
                            </li>
                        );
                    })}
                </ul>
            </div>
        );
        setDateDom(node);
    }, [lang, prefixCls, activeDateNum, onChangeDate, isRunYear]);

    // 点击月份
    // @ts-ignore
    const onChangeMonth = useCallback((monthValue: string, index: number) => {
        if (type === 'hour' || type === 'week') {
            setActiveMonthNum(index);
        } else {
            setMaskShow(false);
            setActiveMonthNum(index);
        }
        const newValue = generateM()(value).set('month', index);
        setSelectValue(newValue);
        // 计算日维度下拉天数
        getDateSelect(activeYearNum, index + 1);
    }, [value, type, setSelectValue, activeYearNum, getDateSelect]);

    const getOptions = useCallback(() => {
        // 获取cascader的options
        const nowDate = new Date();
        const currentYear: number = nowDate.getFullYear();
        const options = [];
        const childrenOptions = [];
        const showCurrentYear = getDisplayYear(generateM()());

        for (let i = 1; i < 13; i++) {
            const obj = {label: i + '月', value: i + '月'};
            childrenOptions.push(obj);
        }
        for (let i = currentYear - 200; i < currentYear + 200; i++) {
            const obj = {label: i - currentYear + showCurrentYear + '年', value: i + '年', children: childrenOptions};
            options.push(obj);
        }
        return options;
    }, []);

    const clickValue = useCallback(() => {
        setMaskShow(prev => !prev);
        const currentYear = value.year();
        const currentMonth = value.month() + 1;
        const options = getOptions();

        options.forEach((item, index) => {
            if (parseInt(item.value) === currentYear) {
                setActiveYearNum(index);
            }
        });

        generateM().locale('zh-cn');
        const monthArr = localeData.monthsShort;
        // @ts-ignore
        monthArr.forEach((item: any, index: number) => {
            if (currentMonth === parseInt(item)) {
                setActiveMonthNum(index);
            }
        });

        setTimeout(() => {
            // 每次面板展开选中项在面板中间
            if (yearUl.current) {
                yearUl.current.scrollTop = (activeYearNum - 2) * 34;
            }
            if (monthUl.current) {
                monthUl.current.scrollTop = (activeMonthNum - 2) * 34;
            }
        }, 0);

        if (type && (type === 'hour' || type === 'week')) {
            const currentDate = value.date();
            setActiveDateNum(currentDate - 1);
            getDateSelect(currentYear, currentMonth);
            setTimeout(() => {
                if (dateUl.current) {
                    dateUl.current.scrollTop = (activeDateNum - 2) * 34;
                }
            }, 0);
        }
    }, [value, type, localeData, activeYearNum, activeMonthNum, activeDateNum, getOptions, getDateSelect]);

    const headerSelect = useCallback(() => {
        const options: {label: string; value: string; children?: any}[] = getOptions();
        generateM().locale('zh-cn');
        const monthData = localeData.monthsShort;
        const node = (
            <div className={`${prefixCls}-header-select`}>
                <div className={`${prefixCls}-header-select-year`} ref={yearUl}>
                    <ul>
                        {options.map((item, index) => {
                            return (
                                <li
                                    className={
                                        activeYearNum == index
                                            ? `${prefixCls}-header-select-year-active`
                                            : undefined
                                    }
                                    key={item.value + index}
                                    onClick={() => onChangeYear(item.value, index)}
                                >
                                    {lang === 'zh-cn' || lang === 'zh-tw'
                                        ? activeYearNum == index
                                            ? parseInt(item.label) + ' 年'
                                            : parseInt(item.label)
                                        : parseInt(item.label)}
                                </li>
                            );
                        })}
                    </ul>
                </div>
                {options[0]?.children && (
                    <div className={`${prefixCls}-header-select-month`} ref={monthUl}>
                        <ul>
                            {(monthData as string[]).map((item: string, index: number) => {
                                return (
                                    <li
                                        className={
                                            activeMonthNum == index
                                                ? `${prefixCls}-header-select-month-active`
                                                : undefined
                                        }
                                        key={item + index}
                                        onClick={() => onChangeMonth(item, index)}
                                    >
                                        {lang === 'zh-cn' || lang === 'zh-tw' ? parseInt(item) : item}
                                        {lang === 'zh-cn' || lang === 'zh-tw'
                                            ? activeMonthNum == index
                                                ? ' 月'
                                                : ''
                                            : ''}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
                {
                    (type === 'hour' || type === 'week') && dateDom
                }
            </div>
        );
        const iconNode = (
            <div className={`${prefixCls}-header-select-icon`}>
                {maskShow ? <Icon type="uf-gridcaretarrowup" /> : <Icon type="uf-gridcaretdown" />}
            </div>
        );
        const boxClass = classnames({
            [`${prefixCls}-header-select-box`]: true,
            [`${prefixCls}-header-select-hour`]: type === 'hour' || type === 'week'
        });
        return (
            <div className={boxClass} ref={boxDom}>
                {maskShow && node}
                <div style={{background: 'transparent', height: '100%'}} onClick={clickValue}>
                    {' '}
                </div>{iconNode}
            </div>
        );
    }, [lang, prefixCls, type, localeData, activeYearNum, activeMonthNum, maskShow, dateDom, onChangeYear, onChangeMonth, clickValue, getOptions]);

    const dom = headerSelect();
    return dom;
};

export default CalendarHeaderSelect;